<template>
  <div class="page-content relacion-page">
    <div
      v-if="cargando"
      class="relacion-loading-overlay"
    >
      <div class="relacion-loading-box">
        <svg
          class="animate-spin h-7 w-7 text-sky-300 mx-auto mb-2"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
        >
          <circle
            class="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            stroke-width="4"
          />
          <path
            class="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8v8z"
          />
        </svg>
        <p class="relacion-loading-text">
          Cargando relaciones…
          <span v-if="relaciones.length">
            ({{ relaciones.length }} cargadas)
          </span>
        </p>
      </div>
    </div>
    <div class="relacion-card">
      <!-- Overlay de carga -->

      <!-- Header -->
      <header class="relacion-header">
        <div>
          <p class="relacion-title">
            Relaciones entre unidades
          </p>
          <p class="relacion-subtitle">
            Visualiza y edita las relaciones entre productos principales y sus complementos.
          </p>
          <p class="relacion-count" v-if="relaciones.length">
            {{ relaciones.length }} unidades base cargadas
          </p>
        </div>

        <div class="relacion-header-actions">
          <button
            type="button"
            class="ios-primary-button relacion-filter-btn"
            @click="modalCategorias = true"
          >
            Filtro de categorías
          </button>
        </div>
      </header>

      <!-- Chips de categorías activas -->
      <div
        v-if="categoriasSeleccionadas.length"
        class="relacion-active-filters"
      >
        <span class="relacion-active-label">
          Categorías activas:
        </span>
        <div class="relacion-active-chips">
          <span
            v-for="cat in categoriasSeleccionadas"
            :key="cat"
            class="relacion-chip"
          >
            {{ cat }}
          </span>
        </div>
      </div>

      <!-- Filtros principales -->
      <section class="relacion-filters">
        <div class="relacion-filters-left">
          <label class="relacion-filter-label">
            Buscar
          </label>
          <div class="relacion-search-wrapper">
            <input
              type="text"
              v-model="busqueda"
              placeholder="Número de serie, producto o complemento…"
              class="relacion-input"
            />
          </div>
        </div>

        <div class="relacion-filters-right">
          <label class="relacion-filter-label">
            Estado
          </label>
          <select
            v-model="filtroEstado"
            class="relacion-select"
          >
            <option value="">Todos los estados</option>
            <option value="almacen">Almacén</option>
            <option value="asignado">Asignado</option>
            <option value="resguardo">Resguardo</option>
            <option value="merma">Merma</option>
            <option value="devuelto">Devuelto</option>
          </select>
        </div>
      </section>

      <!-- Modal filtro categorías -->
      <ModalFiltroCategorias
        v-if="modalCategorias"
        :categorias="categoriasDisponibles"
        :valorInicial="categoriasSeleccionadas"
        @cerrar="modalCategorias = false"
        @aplicar="aplicarFiltroCategorias"
      />

      <!-- Tabla -->
      <section class="relacion-table-wrapper">
        <div v-if="!relacionesFiltradas.length && !cargando" class="relacion-empty">
          No se encontraron relaciones con los filtros actuales.
        </div>

        <div
          v-else
          class="relacion-table-scroll"
        >
          <table class="relacion-table">
            <thead>
              <tr>
                <th class="relacion-th relacion-th--principal">
                  Unidad principal
                </th>
                <th class="relacion-th relacion-th--categoria">
                  Categoría
                </th>
                <th class="relacion-th relacion-th--estado">
                  Estado
                </th>
                <th class="relacion-th relacion-th--complementos">
                  Complementos relacionados
                </th>
                <th class="relacion-th relacion-th--acciones">
                  Acciones
                </th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="relacion in relacionesFiltradas"
                :key="relacion.id_unidad"
                class="relacion-row"
              >
                <!-- Unidad principal -->
                <td class="relacion-td relacion-td--principal">
                  <div class="relacion-main">
                    <div class="relacion-main__bullet"></div>
                    <div class="relacion-main__text">
                      <p class="relacion-main__name">
                        {{ relacion.nombre_producto || 'Producto sin nombre' }}
                      </p>
                      <p class="relacion-main__serial">
                        Serie: {{ relacion.numero_serie }}
                      </p>
                    </div>
                  </div>
                </td>

                <!-- Categoría -->
                <td class="relacion-td relacion-td--categoria">
                  <span
                    v-if="relacion.categoria_nombre"
                    class="relacion-chip relacion-chip--soft"
                  >
                    {{ relacion.categoria_nombre }}
                  </span>
                  <span v-else class="relacion-td-muted">
                    Sin categoría
                  </span>
                </td>

                <!-- Estado -->
                <td class="relacion-td relacion-td--estado">
                  <span
                    class="relacion-status-chip"
                    :class="`relacion-status-chip--${relacion.estado || 'otro'}`"
                  >
                    {{ formatEstado(relacion.estado || 'desconocido') }}
                  </span>

                </td>

                <!-- Complementos -->
                <td class="relacion-td relacion-td--complementos">
                  <div v-if="relacion.complementos && relacion.complementos.length">
                    <div
                      v-for="comp in relacion.complementos"
                      :key="comp.id_unidad"
                      class="relacion-comp-row"
                    >
                      <p class="relacion-comp-row__name">
                        {{ comp.nombre_producto || 'Complemento' }}
                      </p>
                      <p class="relacion-comp-row__serial">
                        {{ comp.numero_serie }}
                      </p>
                    </div>
                  </div>
                  <p v-else class="relacion-td-muted">
                    Sin complementos
                  </p>
                </td>

                <!-- Acciones -->
                <td class="relacion-td relacion-td--acciones">
                  <button
                    type="button"
                    class="relacion-edit-btn"
                    @click="abrirModalEdicion(relacion)"
                  >
                    ✏️
                    <span class="relacion-edit-btn__label">
                      Editar relación
                    </span>
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      <!-- Modal edición -->
      <ModalEditarRelacion
        v-if="modalActivo"
        :unidad="unidadEditando"
        @cerrar="modalActivo = false"
        @guardado="recargar"
      />
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import ModalEditarRelacion from './ModalEditarRelacion.vue';
import ModalFiltroCategorias from './ModalFiltroCategoriasUnidades.vue';

export default {
  name: 'RelacionUnidades',
  components: { ModalEditarRelacion, ModalFiltroCategorias },
  data() {
    return {
      filtroEstado: '',
      relaciones: [],
      busqueda: '',
      unidadEditando: null,
      modalActivo: false,
      cargando: false,
      categoriasDisponibles: [],
      categoriasSeleccionadas: [],
      modalCategorias: false,
    };
  },
  computed: {
    relacionesFiltradas() {
      let base = this.relaciones || [];

      // 1. Filtro por estado
      if (this.filtroEstado) {
        base = base.filter((r) => r.estado === this.filtroEstado);
      }

      // 2. Filtro por categoría
      if (this.categoriasSeleccionadas.length > 0) {
        base = base.filter((r) =>
          this.categoriasSeleccionadas.includes(r.categoria_nombre)
        );
      }

      // 3. Filtro por búsqueda
      if (this.busqueda) {
        const filtro = this.busqueda.toLowerCase();
        base = base.filter((rel) => {
          const principalHit =
            (rel.numero_serie || '').toLowerCase().includes(filtro) ||
            (rel.nombre_producto || '').toLowerCase().includes(filtro);

          const complementos = Array.isArray(rel.complementos)
            ? rel.complementos
            : [];

          const complementosHit = complementos.some((comp) => {
            return (
              (comp.numero_serie || '').toLowerCase().includes(filtro) ||
              (comp.nombre_producto || '').toLowerCase().includes(filtro)
            );
          });

          return principalHit || complementosHit;
        });
      }

      return base;
    },
  },
 
  mounted() {
    this.cargarRelaciones();
  },
  methods: {
      formatEstado(value) {
    if (!value) return 'Desconocido';
    const str = String(value);
    return str.charAt(0).toUpperCase() + str.slice(1);
  },

    aplicarFiltroCategorias(seleccionadas) {
      this.categoriasSeleccionadas = seleccionadas;
      this.modalCategorias = false;
    },
    async cargarRelaciones() {
      this.cargando = true;
      this.relaciones = [];
      const categoriasSet = new Set();

      try {
        const res = await axios.get('/api/almacen/unidades/listar');

        // Unificamos por producto + número de serie
        const unidadesBase = res.data.reduce((acc, unidad) => {
          const existe = acc.find(
            (u) =>
              u.id_producto === unidad.id_producto &&
              u.numero_serie === unidad.numero_serie
          );

          if (!existe) {
            acc.push({
              ...unidad,
              complementos: [],
            });
            if (unidad.categoria_nombre) {
              categoriasSet.add(unidad.categoria_nombre);
            }
          }

          return acc;
        }, []);

        // Ahora rellenamos complementos unidad por unidad
        for (const unidad of unidadesBase) {
          this.relaciones.push(unidad);

          try {
            const resComp = await axios.get(
              '/api/almacen/unidades/por-producto/' + unidad.id_producto
            );
            const detalle = (resComp.data || []).find(
              (item) => item.numero_serie === unidad.numero_serie
            );

            if (detalle) {
              const index = this.relaciones.findIndex(
                (r) => r.id_unidad === unidad.id_unidad
              );
              if (index !== -1) {
                this.relaciones[index].complementos = detalle.complementos || [];
              }
            }
          } catch (errComp) {
            console.warn('Error parcial al cargar complementos:', errComp);
          }
        }
      } catch (err) {
        console.error('Error al cargar relaciones de unidades:', err);
      } finally {
        this.categoriasDisponibles = Array.from(categoriasSet).sort();
        this.cargando = false;
      }
    },
    abrirModalEdicion(unidad) {
      this.unidadEditando = unidad;
      this.modalActivo = true;
    },
    recargar() {
      this.cargarRelaciones();
    },
  },
};
</script>

<style scoped>
.page-content {
  width: 100%;
  display: flex;
  justify-content: center;
  padding: 1.6rem 1rem;
}

.relacion-page {
  min-height: calc(100vh - 64px);
}

.relacion-card {
  position: relative;
  width: 100%;
  max-width: 1120px;
  border-radius: 1.8rem;
  padding: 1.5rem 1.4rem 1.3rem;
  background: radial-gradient(
      circle at 0 0,
      rgba(148, 163, 184, 0.35),
      rgba(15, 23, 42, 0.96)
    );
  backdrop-filter: blur(24px);
  border: 1px solid rgba(148, 163, 184, 0.6);
  box-shadow:
    0 18px 45px rgba(15, 23, 42, 0.9),
    0 0 0 1px rgba(15, 23, 42, 0.85);
}

/* Loader overlay */
.relacion-loading-overlay {
  position: fixed;
  inset: 0;
  border-radius: inherit;
  background: radial-gradient(
    circle at 0 0,
    rgba(15, 23, 42, 0.6),
    rgba(15, 23, 42, 0.96)
  );
  backdrop-filter: blur(10px);
  z-index: 20;
  display: flex;
  align-items: center;
  justify-content: center;
}

.relacion-loading-box {
  padding: 0.85rem 1.1rem;
  border-radius: 1rem;
  background: rgba(15, 23, 42, 0.95);
  border: 1px solid rgba(148, 163, 184, 0.8);
  box-shadow: 0 12px 32px rgba(15, 23, 42, 0.9);
}

.relacion-loading-text {
  font-size: 0.78rem;
  color: #e5e7eb;
}

/* Header */
.relacion-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.85rem;
  margin-bottom: 0.9rem;
}

.relacion-title {
  font-size: 1.1rem;
  font-weight: 600;
  color: #e5e7eb;
}

.relacion-subtitle {
  font-size: 0.8rem;
  color: #94a3b8;
}

.relacion-count {
  margin-top: 0.15rem;
  font-size: 0.77rem;
  color: #cbd5f5;
}

.relacion-header-actions {
  display: flex;
  align-items: center;
  gap: 0.6rem;
}

.relacion-filter-btn {
  font-size: 0.8rem;
  padding-inline: 0.9rem;
}

/* Chips de filtros activos */
.relacion-active-filters {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.35rem;
  margin-bottom: 0.9rem;
}

.relacion-active-label {
  font-size: 0.77rem;
  color: #cbd5f5;
}

.relacion-active-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 0.3rem;
}

.relacion-chip {
  font-size: 0.72rem;
  padding: 0.2rem 0.5rem;
  border-radius: 999px;
  border: 1px solid rgba(148, 163, 184, 0.8);
  color: #e5e7eb;
  background: rgba(15, 23, 42, 0.92);
}

.relacion-chip--soft {
  border-color: rgba(148, 163, 184, 0.55);
  background: rgba(15, 23, 42, 0.88);
}

/* Filtros */
.relacion-filters {
  display: flex;
  flex-wrap: wrap;
  gap: 0.85rem;
  margin-bottom: 0.9rem;
}

.relacion-filters-left {
  flex: 1 1 220px;
  min-width: 0;
}

.relacion-filters-right {
  width: 200px;
}

.relacion-filter-label {
  display: block;
  font-size: 0.75rem;
  color: #cbd5f5;
  margin-bottom: 0.2rem;
}

.relacion-search-wrapper {
  position: relative;
}

.relacion-input {
  width: 100%;
  border-radius: 999px;
  border: 1px solid rgba(148, 163, 184, 0.7);
  background: rgba(15, 23, 42, 0.96);
  padding: 0.45rem 0.8rem;
  font-size: 0.8rem;
  color: #e5e7eb;
}

.relacion-input::placeholder {
  color: #64748b;
}

.relacion-select {
  width: 100%;
  border-radius: 999px;
  border: 1px solid rgba(148, 163, 184, 0.7);
  background: rgba(15, 23, 42, 0.96);
  padding: 0.42rem 0.75rem;
  font-size: 0.78rem;
  color: #e5e7eb;
}

/* Tabla */
.relacion-table-wrapper {
  border-radius: 1.3rem;
  border: 1px solid rgba(148, 163, 184, 0.5);
  background: rgba(15, 23, 42, 0.94);
  padding: 0.7rem 0.7rem 0.6rem;
  min-height: 120px;
}

.relacion-empty {
  font-size: 0.8rem;
  color: #e5e7eb;
  padding: 0.4rem 0.25rem;
}

.relacion-table-scroll {
  width: 100%;
  overflow-x: auto;
}

/* scroll horizontal tabla */
.relacion-table-scroll::-webkit-scrollbar {
  height: 6px;
}
.relacion-table-scroll::-webkit-scrollbar-track {
  background: rgba(15, 23, 42, 1);
}
.relacion-table-scroll::-webkit-scrollbar-thumb {
  background: rgba(148, 163, 184, 0.75);
  border-radius: 999px;
}

/* Tabla */
.relacion-table {
  width: 100%;
  border-collapse: collapse;
  min-width: 720px;
}

.relacion-th {
  text-align: left;
  padding: 0.5rem 0.65rem;
  font-size: 0.75rem;
  font-weight: 500;
  color: #cbd5f5;
  border-bottom: 1px solid rgba(148, 163, 184, 0.5);
  white-space: nowrap;
}

.relacion-th--principal {
  width: 26%;
}
.relacion-th--categoria {
  width: 18%;
}
.relacion-th--estado {
  width: 14%;
}
.relacion-th--complementos {
  width: 32%;
}
.relacion-th--acciones {
  width: 10%;
  text-align: right;
}

.relacion-row {
  border-bottom: 1px solid rgba(30, 41, 59, 0.85);
}

.relacion-td {
  padding: 0.45rem 0.65rem;
  font-size: 0.78rem;
  color: #e5e7eb;
  vertical-align: top;
}

.relacion-td--acciones {
  text-align: right;
}

.relacion-td-muted {
  font-size: 0.74rem;
  color: #9ca3af;
}

/* Unidad principal */
.relacion-main {
  display: flex;
  align-items: center;
  gap: 0.45rem;
}

.relacion-main__bullet {
  width: 9px;
  height: 9px;
  border-radius: 999px;
  background: radial-gradient(circle, #22c55e, #16a34a);
  box-shadow: 0 0 8px rgba(34, 197, 94, 0.9);
}

.relacion-main__text {
  min-width: 0;
}

.relacion-main__name {
  font-size: 0.8rem;
  font-weight: 500;
  color: #e5e7eb;
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
}

.relacion-main__serial {
  margin-top: 1px;
  font-size: 0.72rem;
  color: #9ca3af;
}

/* Complementos */
.relacion-comp-row {
  padding: 0.3rem 0.45rem;
  border-radius: 0.7rem;
  border: 1px solid rgba(148, 163, 184, 0.45);
  background: rgba(15, 23, 42, 0.95);
  margin-bottom: 0.25rem;
}

.relacion-comp-row__name {
  font-size: 0.78rem;
  color: #e5e7eb;
}

.relacion-comp-row__serial {
  font-size: 0.7rem;
  color: #9ca3af;
}

/* Estado chip */
.relacion-status-chip {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.18rem 0.55rem;
  border-radius: 999px;
  font-size: 0.7rem;
  border: 1px solid rgba(148, 163, 184, 0.7);
}

/* Colores por estado (ajusta si lo deseas) */
.relacion-status-chip--almacen {
  border-color: rgba(59, 130, 246, 0.9);
  background: rgba(59, 130, 246, 0.15);
  color: #bfdbfe;
}

.relacion-status-chip--asignado {
  border-color: rgba(34, 197, 94, 0.85);
  background: rgba(22, 163, 74, 0.18);
  color: #bbf7d0;
}

.relacion-status-chip--resguardo {
  border-color: rgba(234, 179, 8, 0.9);
  background: rgba(234, 179, 8, 0.16);
  color: #facc15;
}

.relacion-status-chip--merma {
  border-color: rgba(248, 113, 113, 0.9);
  background: rgba(248, 113, 113, 0.14);
  color: #fecaca;
}

.relacion-status-chip--devuelto {
  border-color: rgba(129, 140, 248, 0.9);
  background: rgba(129, 140, 248, 0.16);
  color: #c7d2fe;
}

.relacion-status-chip--otro {
  border-color: rgba(148, 163, 184, 0.8);
  background: rgba(15, 23, 42, 0.9);
  color: #e5e7eb;
}

/* Botón editar */
.relacion-edit-btn {
  border-radius: 999px;
  border: 1px solid rgba(148, 163, 184, 0.8);
  background: rgba(15, 23, 42, 0.96);
  color: #e5e7eb;
  font-size: 0.75rem;
  padding: 0.25rem 0.6rem;
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  cursor: pointer;
  transition:
    background 0.16s ease,
    border-color 0.16s ease,
    box-shadow 0.16s ease,
    transform 0.06s ease;
}

.relacion-edit-btn__label {
  display: none;
}

.relacion-edit-btn:hover {
  background: rgba(15, 23, 42, 1);
  box-shadow: 0 10px 26px rgba(15, 23, 42, 0.95);
}

.relacion-edit-btn:active {
  transform: translateY(0);
  box-shadow: 0 5px 16px rgba(15, 23, 42, 1);
}

/* Responsive */
@media (max-width: 768px) {
  .relacion-header {
    flex-direction: column;
    align-items: flex-start;
  }

  .relacion-filters {
    flex-direction: column;
  }

  .relacion-filters-right {
    width: 100%;
  }

  .relacion-edit-btn__label {
    display: none;
  }
}
</style>
